import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto } from '@nusign/picflow-backend-types';
import { catchError, firstValueFrom, map, throwError, timer } from 'rxjs';
import { getFileContentType } from '../lib/get-file-type';
import { WITH_AUTH } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { GOOGLE_API_KEY, GoogleApiService } from './google-api.service';
import { UploadService } from './upload.service';

export interface GoogleDriveFile {
  name: string;
  id: string;
  size: number;
  source: 'google-drive';
}

const SCOPES = 'https://www.googleapis.com/auth/drive.file';

@Injectable({
  providedIn: 'root',
})
export class GoogleDriveService {
  private http = inject(HttpClient);
  private config = inject(PICFLOW_CONFIG);
  private uploadService = inject(UploadService);
  private googleApiService = inject(GoogleApiService);
  private accessToken: string;

  constructor() {
    this.uploadService.alternativeUploadServices.push({
      source: 'google-drive',
      service: this,
    });
  }

  public async uploadFile(
    file: GoogleDriveFile,
    galleryId: string,
    sectionId: string,
    position: number,
    assetToReplace?: AssetDto,
    version_name?: string
  ): Promise<AssetDto> {
    return firstValueFrom(
      this.http
        .post<AssetDto>(
          this.config.internalApiUrlBase + '/upload-from-google-drive',
          {
            gallery: galleryId,
            section: sectionId,
            asset_name: file.name,
            position,
            id: file.id,
            content_type: getFileContentType(file),
            asset_to_replace: assetToReplace?.id,
            google_drive_token: this.accessToken,
            version_name,
          },
          { context: new HttpContext().set(WITH_AUTH, true) }
        )
        .pipe(
          catchError(err => {
            if (err instanceof HttpErrorResponse) {
              if (err.status === 503) {
                return timer(10_000).pipe(map(() => undefined));
              }
            }
            return throwError(() => err);
          })
        )
    );
  }

  private showPicker(): Promise<GoogleDriveFile[]> {
    return new Promise(resolve => {
      const picker = new window.google.picker.PickerBuilder()
        .addView(window.google.picker.ViewId.DOCS_IMAGES_AND_VIDEOS)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
        .enableFeature(window.google.picker.Feature.SUPPORT_TEAM_DRIVES)
        .setOAuthToken(this.accessToken)
        .setDeveloperKey(GOOGLE_API_KEY)
        .setCallback(data => {
          if (data.action == 'picked') {
            resolve(
              data.docs.map(doc => {
                return <GoogleDriveFile>{
                  name: doc.name,
                  size: doc.sizeBytes,
                  id: doc.id,
                  source: 'google-drive',
                };
              })
            );
          } else if (data.action == 'cancel') {
            resolve([]);
          }
        })
        .build();
      picker.setVisible(true);
    });
  }

  public async startChooser(): Promise<GoogleDriveFile[]> {
    this.accessToken = await this.googleApiService.requestAccess(SCOPES, 'picker');
    return this.showPicker();
  }
}
